import { FormikProvider, useFormik } from 'formik';
import { delay } from 'lodash';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import { CURRENT_FOLDER_QK } from '@/modules/admin-dashboard/pages/Documents.tsx';
import {
  useUpdateDocumentMutation,
  useUploadDocumentMutation,
} from '@/modules/admin-dashboard/queries/documents.ts';
import {
  AcceptFormat,
  BaseModalProps,
  Button,
  Dropzone,
  ModalContentWrapper,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { useParseNumericQueryParam } from 'ui/hooks';
import { getFileNameFromBase64, getFileNameWithoutExtension } from 'ui/utils';
import { UserDocumentResponse } from 'compliance-api';

export const acceptFormat: AcceptFormat[] = [
  'doc',
  'xls',
  'csv',
  'pdf',
  'png',
  'jpeg',
  'bmp',
  'odt',
  'html',
  'ppt',
  'tiff',
  'txt',
  'webp',
  'gif',
  'msg',
];

export const viewableFormat: string[] = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'bmp',
  'odt',
  'htm',
  'html',
  'ppt',
  'pptx',
  'tif',
  'tiff',
  'txt',
  'webp',
  'gif',
];

const getInitialValues = (initialDocument?: UserDocumentResponse) => {
  return {
    name: initialDocument?.name || '',
    description: initialDocument?.description || '',
    file_data: '',
    client_id: initialDocument?.client_id || undefined,
    clientOption: initialDocument?.client
      ? {
          value: initialDocument.client.id,
          label: initialDocument.client.name,
        }
      : undefined,
    initialDocument: initialDocument || null,
  };
};

const validationSchema = yup.object({
  name: yup.string().trim().min(3).max(100).required('Name is required'),
  file_data: yup.string().when(['initialDocument'], ([initialDocument], schema) => {
    return initialDocument ? schema.nullable() : schema.required('Select file');
  }),
  initialDocument: yup.object().nullable(),
});

interface DocumentModalProps extends BaseModalProps {
  initialDocument?: UserDocumentResponse;
  clientId?: number;
  hideClientSelector?: boolean;
}

const DocumentModal = ({
  initialDocument,
  open,
  onClose,
  clientId,
  hideClientSelector,
}: DocumentModalProps) => {
  const [isFileReading, setIsFileReading] = useState(false);
  const currentFolderId = useParseNumericQueryParam(CURRENT_FOLDER_QK);

  const { mutateAsync: uploadDocument, isLoading: isDocumentUploading } =
    useUploadDocumentMutation();

  const { mutateAsync: updateDocument, isLoading: isDocumentUpdating } =
    useUpdateDocumentMutation();

  const formik = useFormik({
    initialValues: {
      ...getInitialValues(initialDocument),
      // Override with clientId from props if provided (when on client page)
      client_id: clientId || initialDocument?.client_id || undefined,
      clientOption:
        clientId && !initialDocument
          ? undefined // Don't need option when clientId is forced
          : initialDocument?.client
          ? {
              value: initialDocument.client.id,
              label: initialDocument.client.name,
            }
          : undefined,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      if (!initialDocument) {
        await uploadDocument({
          name: values.name,
          description: values.description,
          file_data: values.file_data,
          folder_id: currentFolderId || undefined,
          client_id: clientId || values.client_id,
        });
      } else {
        await updateDocument({
          document_id: initialDocument.id,
          name: values.name,
          description: values.description,
          client_id: clientId || values.client_id,
        });
      }
      onClose();
    },
  });

  const handleAcceptFile = useCallback(
    async (file: File) => {
      setIsFileReading(true);
      const base64 = await fileToBase64(file, true);

      await formik.setFieldValue('file_data', base64);

      if (!formik.values.name.trim()) {
        await formik.setFieldValue('name', getFileNameWithoutExtension(file.name));
        delay(formik.validateField, 1, 'name');
      }

      delay(formik.validateField, 1, 'file_data');

      setIsFileReading(false);
    },
    [formik],
  );

  const handleClearFile = useCallback(() => {
    formik.setFieldValue('file_data', '');
  }, [formik]);

  const handleSelectClient = useCallback(
    (newValue: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client_id', newValue?.value || undefined);
      formik.setFieldValue('clientOption', newValue);
    },
    [formik],
  );

  return (
    <ModalContentWrapper title="Upload document" open={open} onClose={onClose}>
      <FormikProvider value={formik}>
        <div className="space-y-4">
          <TextInputField
            label="Name"
            placeholder="Name..."
            name="name"
            error={formik.errors.name}
          />
          <TextInputField label="description" placeholder="Description..." name="description" />
          {!clientId && !hideClientSelector && (
            <ClientsSelectPaginated
              isMulti={false}
              value={formik.values.clientOption}
              onChange={handleSelectClient}
              isClearable={true}
            />
          )}
          {!initialDocument && (
            <Dropzone
              customUnsupportedFormatError="Please upload a supported format: images (JPG, PNG, GIF, WEBP), documents (PDF, DOC, DOCX, MSG), spreadsheets (XLS, XLSX), presentations (PPT, PPTX), or text files (TXT, CSV)."
              maxSizeMb={55}
              value={formik.values.file_data}
              acceptFormat={acceptFormat}
              loading={isFileReading}
              onFileAccepted={handleAcceptFile}
              onFileClear={handleClearFile}
              error={formik.errors.file_data}
              fileName={getFileNameFromBase64(formik.values.file_data)}
            />
          )}
          <Button
            disabled={isDocumentUploading || isDocumentUpdating}
            loading={isDocumentUploading || isDocumentUpdating}
            onClick={formik.handleSubmit}
          >
            {initialDocument ? 'Update document information' : 'Upload Document'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default DocumentModal;
